/**
 * Bettr Add-ons — PDP cross-sell controller
 *
 * Drives the "Complete Your Setup" cards on the sauna PDP.
 * - Tracks selected addon variants
 * - Bundles them into the sauna ATC submission via buildCartItems()
 * - Opens the addon detail modal on "Learn more"
 * - Validates cart on every cart change to remove orphan addons
 *
 * Single global: window.BettrAddons
 */
(function () {
  'use strict';

  /* ── Selection state (module-private) ── */
  var selected = new Map();

  /* ── DOM wiring ── */
  function wireCheckbox(check) {
    if (check.hasAttribute('data-bettr-wired')) return;
    check.setAttribute('data-bettr-wired', '1');

    check.addEventListener('change', function (e) {
      var t = e.target;
      var variantId = t.getAttribute('data-addon-variant-id');
      var productId = t.getAttribute('data-addon-product-id');
      var type = t.getAttribute('data-addon-type');
      var card = t.closest('[data-addon-card]');

      if (t.checked) {
        selected.set(variantId, { productId: productId, type: type });
        if (card) card.classList.add('is-checked');
      } else {
        selected.delete(variantId);
        if (card) card.classList.remove('is-checked');
      }
    });
  }

  function wireModalTrigger(trigger) {
    if (trigger.hasAttribute('data-bettr-wired')) return;
    trigger.setAttribute('data-bettr-wired', '1');

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      var handle = trigger.getAttribute('data-product-handle');
      if (!handle) return;
      if (typeof window.BettrAddons.openModal === 'function') {
        window.BettrAddons.openModal(handle, trigger);
      }
    });
  }

  /**
   * Card-level click handler — clicking anywhere inside the card's visual
   * bounds toggles the addon checkbox. We skip when the real target is an
   * interactive child (checkbox label or "Learn more" button) so those
   * keep their own behavior.
   */
  function wireCard(card) {
    if (card.hasAttribute('data-bettr-card-wired')) return;
    card.setAttribute('data-bettr-card-wired', '1');

    card.addEventListener('click', function (e) {
      /* Let the Learn more button open the modal without toggling selection */
      if (e.target.closest('[data-addon-modal-trigger]')) return;
      /* The label already triggers the checkbox natively — don't double-toggle */
      if (e.target.closest('.pdp-addons__check-wrap')) return;
      var check = card.querySelector('.pdp-addons__check');
      if (check) check.click();
    });
  }

  /**
   * Extended hit-area — clicks that land within 20px (desktop) or 15px (mobile)
   * of an add-on card's visual edge still toggle that card's checkbox.
   *
   * Uses a document-level listener that reads each card's bounding rect at
   * click time (no layout thrash on idle). Runs only on clicks that fall
   * OUTSIDE any card — inside-card clicks go through `wireCard` normally.
   *
   * Implemented in JS instead of a CSS ::before pseudo so the extension zone
   * isn't at the mercy of stacking contexts or the card's overflow:hidden
   * (which is needed for the is-checked gradient-border clip + badge corner).
   */
  function wireCardHitArea() {
    if (wireCardHitArea._wired) return;
    wireCardHitArea._wired = true;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      /* Clicks landing on any card (or its children) are handled elsewhere */
      if (t.closest('[data-addon-card]')) return;
      /* Don't swallow Learn more or checkbox clicks if, somehow, they land outside a card */
      if (t.closest('[data-addon-modal-trigger]')) return;
      if (t.closest('.pdp-addons__check-wrap')) return;
      /* Only plain page background next to a card counts. A click on a control
         or link, inside an open panel or its backdrop, or on anything in a fixed
         layer (the sticky Add to Cart bar, the header, chat) belongs to that
         thing. Without this, tapping the sticky Add to Cart while the Sauna
         Assembly card sat behind it ticked the $880 service into the order
         (reproduced live 2026-10-02), and closing a panel opened over the cards
         toggled them too. */
      if (t.closest('[popover], dialog, [role="dialog"], button, a, input, select, textarea, label, summary, [role="button"]')) return;
      for (var n = t; n && n !== document.body; n = n.parentElement) {
        if (window.getComputedStyle(n).position === 'fixed') return;
      }

      var extension = window.matchMedia('(max-width: 749px)').matches ? 15 : 20;
      var cards = document.querySelectorAll('[data-addon-card]');
      var x = e.clientX;
      var y = e.clientY;

      for (var i = 0; i < cards.length; i++) {
        var r = cards[i].getBoundingClientRect();
        if (
          x >= r.left - extension && x <= r.right + extension &&
          y >= r.top - extension && y <= r.bottom + extension
        ) {
          var check = cards[i].querySelector('.pdp-addons__check');
          if (check) check.click();
          return; /* toggle only the first match */
        }
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-addon-variant-id]').forEach(wireCheckbox);
    document.querySelectorAll('[data-addon-modal-trigger]').forEach(wireModalTrigger);
    document.querySelectorAll('[data-addon-card]').forEach(wireCard);
    wireCardHitArea();
    /* Prefetch product data so the modal opens with content on the first paint */
    prefetchAllAddons();
  }

  /* ── Cart payload helpers ── */

  /**
   * Items array for /cart/add.js — selected addons only.
   * Each addon carries an `_addon_type` line item property
   * so the cart validator can identify and orphan-cleanup later.
   */
  function getSelectedItems() {
    var items = [];
    selected.forEach(function (meta, variantId) {
      items.push({
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: {
          _addon_type: meta.type
        }
      });
    });
    return items;
  }

  /**
   * Sauna line + selected addons, in one array, ready for
   * fetch('/cart/add.js') with a single round trip.
   */
  function buildCartItems(saunaVariantId) {
    var items = [{ id: parseInt(saunaVariantId, 10), quantity: 1 }];
    return items.concat(getSelectedItems());
  }

  /* ────────────── Modal ────────────── */

  var modalState = {
    el: null,
    mediaBlock: null,
    image: null,
    title: null,
    subhead: null,
    priceRow: null,
    description: null,
    counter: null,
    prev: null,
    next: null,
    media: [],
    index: 0,
    lastFocused: null,
    keydownHandler: null,
    overlayHandler: null
  };

  /* Lazy-render: the modal markup lives in <template id="addon-modal-template">
     in snippets/custom-addon-modal.liquid. We clone it into <body> on the
     first getModalEl() call. Saves ~6 KB HTML / ~30 DOM nodes on every
     page that renders the snippet (i.e. every page — it's in theme.liquid). */
  function getModalEl() {
    if (modalState.el && modalState.el.isConnected) return modalState.el;
    var el = document.querySelector('[data-addon-modal]');
    if (!el) {
      var tmpl = document.getElementById('addon-modal-template');
      if (!tmpl || !tmpl.content) return null;
      document.body.appendChild(tmpl.content.cloneNode(true));
      el = document.querySelector('[data-addon-modal]');
      if (!el) return null;
    }
    modalState.el = el;
    modalState.mediaBlock = el.querySelector('[data-addon-modal-media]');
    modalState.image = el.querySelector('[data-addon-modal-image]');
    modalState.title = el.querySelector('[data-addon-modal-title]');
    modalState.subhead = el.querySelector('[data-addon-modal-subhead]');
    modalState.priceRow = el.querySelector('[data-addon-modal-price-row]');
    modalState.description = el.querySelector('[data-addon-modal-description]');
    modalState.counter = el.querySelector('[data-addon-modal-counter]');
    modalState.prev = el.querySelector('[data-addon-modal-prev]');
    modalState.next = el.querySelector('[data-addon-modal-next]');
    modalState.addBtn = el.querySelector('[data-addon-modal-add]');
    return el;
  }

  /* Find the on-page add-on card checkbox for a given product handle.
     The handle lives on the modal trigger inside each card. */
  function findAddonCheckbox(handle) {
    if (!handle) return null;
    var trigger = document.querySelector('[data-product-handle="' + handle + '"]');
    var card = trigger ? trigger.closest('[data-addon-card]') : null;
    return card ? card.querySelector('.pdp-addons__check') : null;
  }

  /* Sync the modal's Add CTA to the current add-on's selection state. */
  function syncAddCta() {
    if (!modalState.addBtn) return;
    var check = findAddonCheckbox(modalState.currentHandle);
    if (!check) { modalState.addBtn.hidden = true; return; }
    modalState.addBtn.hidden = false;
    if (check.checked) {
      modalState.addBtn.textContent = 'Added to your sauna — remove';
      modalState.addBtn.classList.add('addon-modal__add--added');
    } else {
      modalState.addBtn.textContent = 'Add to my sauna';
      modalState.addBtn.classList.remove('addon-modal__add--added');
    }
  }

  /* Clear the page-side add-on selection after the cart loses its parent
     sauna, so a later ATC doesn't silently re-attach add-ons the buyer
     already removed. Direct state sync — deliberately NO synthetic change
     events (they would re-enter the selection handlers). */
  function resetSelection() {
    if (selected.size) selected.clear();
    document.querySelectorAll('[data-addon-card]').forEach(function (card) {
      var check = card.querySelector('.pdp-addons__check');
      if (check && check.checked) check.checked = false;
      card.classList.remove('is-checked');
    });
    syncAddCta();
  }

  /**
   * Per-product display rewrites for the modal.
   * Returns {title, subhead}. Defaults to product.title with no subhead.
   */
  function getModalCopy(p) {
    var handle = p.handle || '';
    /* Bettr Protect — sentence-case the title, derive size from handle. */
    var warrantyMatch = handle.match(/^bettr-protect-(\d+)p$/);
    if (warrantyMatch) {
      return {
        title: 'BettrProtect - 5 years',
        subhead: 'Ritual ' + warrantyMatch[1] + ' coverage'
      };
    }
    /* Sauna Assembly — installation-style subhead. ("Two-person installation"
       meant a two-person crew but read as sauna size on the 4P/8P PDPs.) */
    if (handle === 'sauna-assembly-service') {
      return {
        title: p.title || 'Sauna Assembly Service',
        subhead: 'Professional on-site assembly'
      };
    }
    /* Outdoor Shower — material-and-function subhead. */
    if (handle === 'bettr-outdoor-shower') {
      return {
        title: p.title || 'Outdoor Shower',
        subhead: 'Cedar rinse station'
      };
    }
    return { title: p.title || '', subhead: '' };
  }

  function formatMoney(cents) {
    /* Shopify cents (integer) → "$1,234" (no trailing zeros for whole dollars,
       2 decimals only when needed). Matches money_without_trailing_zeros filter. */
    var dollars = (cents / 100);
    var minFraction = (cents % 100 === 0) ? 0 : 2;
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: minFraction,
      maximumFractionDigits: 2
    });
  }

  /* Per-handle zoom table. Some product PNGs ship with heavy built-in
     whitespace; we compensate with a CSS transform instead of re-exporting.
     Scale actual enforced in snippets/custom-addon-modal.liquid. */
  var ZOOM_BY_HANDLE = {
    'bettr-outdoor-shower': 'shower'
  };

  function applyZoom(handle) {
    if (!modalState.image) return;
    var zoom = ZOOM_BY_HANDLE[handle];
    if (zoom) {
      modalState.image.setAttribute('data-zoom', zoom);
    } else {
      modalState.image.removeAttribute('data-zoom');
    }
  }

  function renderMedia() {
    if (!modalState.image || !modalState.mediaBlock) return;

    /* Hide the entire image block when there are no images (e.g. warranty add-on). */
    if (!modalState.media.length) {
      modalState.mediaBlock.hidden = true;
      modalState.image.removeAttribute('src');
      modalState.image.alt = '';
      modalState.counter.textContent = '';
      modalState.prev.hidden = true;
      modalState.next.hidden = true;
      return;
    }

    modalState.mediaBlock.hidden = false;
    var item = modalState.media[modalState.index];
    /* Set width/height BEFORE src so the browser reserves layout — prevents CLS. */
    if (item.width && item.height) {
      modalState.image.setAttribute('width', item.width);
      modalState.image.setAttribute('height', item.height);
    } else {
      modalState.image.removeAttribute('width');
      modalState.image.removeAttribute('height');
    }
    modalState.image.src = item.src;
    modalState.image.alt = item.alt || '';

    if (modalState.media.length > 1) {
      modalState.counter.textContent = (modalState.index + 1) + ' / ' + modalState.media.length;
      modalState.prev.hidden = false;
      modalState.next.hidden = false;
    } else {
      modalState.counter.textContent = '';
      modalState.prev.hidden = true;
      modalState.next.hidden = true;
    }
  }

  function step(delta) {
    if (modalState.media.length < 2) return;
    var n = modalState.media.length;
    modalState.index = (modalState.index + delta + n) % n;
    renderMedia();
  }

  /* P4.2 — FOCUSABLE + trapFocus removed. Native <dialog>.showModal()
     traps focus across the top-layer boundary without any JS help. */

  function onKeydown(e) {
    /* Native <dialog> handles Esc via `cancel` event (wired in wireModal)
       and focus trap via showModal top-layer semantics. We only own the
       arrow-key gallery navigation. */
    if (e.key === 'ArrowLeft') {
      step(-1);
    } else if (e.key === 'ArrowRight') {
      step(1);
    }
  }

  /* Product data cache + prefetcher.
     We pre-fetch every add-on's data in the background on page load so the
     modal opens with content already available — no flash of empty frame. */
  var productCache = new Map();
  var productPending = new Map();

  function fetchProduct(handle) {
    if (productPending.has(handle)) return productPending.get(handle);
    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        productCache.set(handle, p);
        productPending.delete(handle);
        return p;
      })
      .catch(function (err) {
        productPending.delete(handle);
        throw err;
      });
    productPending.set(handle, promise);
    return promise;
  }

  function prefetchAllAddons() {
    document.querySelectorAll('[data-addon-modal-trigger]').forEach(function (t) {
      var h = t.getAttribute('data-product-handle');
      if (h && !productCache.has(h) && !productPending.has(h)) fetchProduct(h);
    });
  }

  /* Populate modal DOM from a product object — synchronous, used by openModal. */
  function populateModal(p) {
    if (!modalState.el || !modalState.el.classList.contains('is-open')) return;

    var copy = getModalCopy(p);
    modalState.title.textContent = copy.title;
    if (copy.subhead) {
      modalState.subhead.textContent = copy.subhead;
      modalState.subhead.hidden = false;
    } else {
      modalState.subhead.textContent = '';
      modalState.subhead.hidden = true;
    }

    var priceHtml = '<span class="addon-modal__price">' + formatMoney(p.price) + '</span>';
    if (p.compare_at_price && p.compare_at_price > p.price) {
      priceHtml += '<s class="addon-modal__rrp">' + formatMoney(p.compare_at_price) + '</s>';
    }
    modalState.priceRow.innerHTML = priceHtml;

    modalState.description.innerHTML = p.description || '';

    var media = [];
    if (p.media && p.media.length) {
      for (var i = 0; i < p.media.length; i++) {
        var m = p.media[i];
        if (m.media_type === 'image' && m.preview_image) {
          media.push({
            src: m.preview_image.src,
            alt: m.alt || p.title,
            width: m.preview_image.width || null,
            height: m.preview_image.height || null
          });
        }
      }
    }
    if (!media.length && p.featured_image) {
      media.push({ src: p.featured_image, alt: p.title, width: null, height: null });
    }
    modalState.media = media;
    modalState.index = 0;
    applyZoom(p.handle);
    renderMedia();
  }

  function openModal(handle, trigger) {
    var el = getModalEl();
    if (!el || !handle) return;
    /* Lazy-clone path: wire close/overlay/prev/next handlers the first
       time we touch the modal. Idempotent via data-bettr-modal-wired. */
    wireModal();

    /* Phase 21 / P1.4 — User Timing mark for add-on modal open.
       Visible in DevTools Performance panel, reported via BettrMetrics. */
    if (window.BettrMetrics && window.BettrMetrics.mark) {
      window.BettrMetrics.mark('addon-modal-open');
    }

    /* If the modal is already open (e.g. user clicked a different add-on
       Learn-more before closing), only swap content. Re-saving the scroll
       lock state would capture our own 'hidden' values and clobber the
       original page state on close. */
    var alreadyOpen = el.classList.contains('is-open');

    modalState.lastFocused = trigger || document.activeElement;
    modalState.currentHandle = handle;

    var cached = productCache.get(handle);

    if (cached) {
      /* Cache hit — populate BEFORE the panel paints, so content is there on first frame */
      populateOnHiddenModal(cached);
    } else {
      /* Cache miss — clear stale content so the previous product doesn't briefly show */
      modalState.title.textContent = '';
      modalState.subhead.textContent = '';
      modalState.subhead.hidden = true;
      modalState.priceRow.innerHTML = '';
      modalState.description.innerHTML = '';
      modalState.media = [];
      modalState.index = 0;
      renderMedia();
    }

    if (!alreadyOpen) {
      /* P4.2 — native <dialog>.showModal() traps focus across the top
         layer, handles Esc natively (we intercept via `cancel` below to
         wrap close in our cleanup), and renders above every other page
         element. Kept `.is-open` in sync for the existing :has() scroll
         lock and @starting-style fade. */
      try { el.showModal(); } catch (_) { /* already open or mid-close */ }
    }
    el.classList.add('is-open');
    syncAddCta();

    if (!alreadyOpen) {
      /* Arrow-key gallery nav only (Esc + Tab handled by native dialog).
         Listener attached to the dialog so keydowns escape the top layer
         to reach us. */
      modalState.keydownHandler = onKeydown;
      el.addEventListener('keydown', modalState.keydownHandler);
    }

    var closeBtn = el.querySelector('[data-addon-modal-close]');
    if (closeBtn) closeBtn.focus();

    /* On miss: fetch then populate. On hit: nothing more to do. */
    if (!cached) {
      fetchProduct(handle)
        .then(function (p) { populateModal(p); })
        .catch(function () {
          modalState.description.innerHTML = '<p>Unable to load product details. Please try again.</p>';
        });
    }
  }

  /* Populate while the modal is closed (cache-hit path).
     populateModal() bails if not open, so we use a direct write here. */
  function populateOnHiddenModal(p) {
    var copy = getModalCopy(p);
    modalState.title.textContent = copy.title;
    if (copy.subhead) {
      modalState.subhead.textContent = copy.subhead;
      modalState.subhead.hidden = false;
    } else {
      modalState.subhead.textContent = '';
      modalState.subhead.hidden = true;
    }

    var priceHtml = '<span class="addon-modal__price">' + formatMoney(p.price) + '</span>';
    if (p.compare_at_price && p.compare_at_price > p.price) {
      priceHtml += '<s class="addon-modal__rrp">' + formatMoney(p.compare_at_price) + '</s>';
    }
    modalState.priceRow.innerHTML = priceHtml;

    modalState.description.innerHTML = p.description || '';

    var media = [];
    if (p.media && p.media.length) {
      for (var i = 0; i < p.media.length; i++) {
        var m = p.media[i];
        if (m.media_type === 'image' && m.preview_image) {
          media.push({
            src: m.preview_image.src,
            alt: m.alt || p.title,
            width: m.preview_image.width || null,
            height: m.preview_image.height || null
          });
        }
      }
    }
    if (!media.length && p.featured_image) {
      media.push({ src: p.featured_image, alt: p.title, width: null, height: null });
    }
    modalState.media = media;
    modalState.index = 0;
    applyZoom(p.handle);
    renderMedia();
  }

  function closeModal() {
    var el = getModalEl();
    if (!el) return;
    if (!el.classList.contains('is-open')) return;

    el.classList.remove('is-open');

    /* Native dialog close — guarded because calling close() on an
       already-closed dialog throws in some browsers. */
    if (typeof el.close === 'function' && el.open) {
      try { el.close(); } catch (_) {}
    }

    /* Scroll lock release happens automatically when .is-open is removed
       (the centralized :has() rule no longer matches). */

    /* Listeners — arrow-key gallery nav */
    if (modalState.keydownHandler) {
      el.removeEventListener('keydown', modalState.keydownHandler);
      modalState.keydownHandler = null;
    }

    /* Restore focus */
    if (modalState.lastFocused && typeof modalState.lastFocused.focus === 'function') {
      modalState.lastFocused.focus();
    }
    modalState.lastFocused = null;

    /* Clear content (defensive — modal hidden anyway) */
    modalState.media = [];
    modalState.index = 0;
  }

  /* Wire close + overlay + nav buttons (one-time on first init) */
  function wireModal() {
    var el = getModalEl();
    if (!el || el.hasAttribute('data-bettr-modal-wired')) return;
    el.setAttribute('data-bettr-modal-wired', '1');

    var closeBtn = el.querySelector('[data-addon-modal-close]');
    var overlay = el.querySelector('[data-addon-modal-overlay]');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
    if (modalState.prev) modalState.prev.addEventListener('click', function () { step(-1); });
    if (modalState.next) modalState.next.addEventListener('click', function () { step(1); });
    if (modalState.addBtn) {
      modalState.addBtn.addEventListener('click', function () {
        var check = findAddonCheckbox(modalState.currentHandle);
        if (!check) return;
        var wasChecked = check.checked;
        check.click(); /* fires the native change → updates selection Map + card state */
        syncAddCta();
        /* Adding it → close so they return to the buy flow; removing → stay so they can reconsider */
        if (!wasChecked) closeModal();
      });
    }

    /* P4.2 — intercept native Esc so our cleanup (class sync + focus
       restore + gallery reset) runs before the dialog closes. Without
       preventDefault, the dialog would close but leave the .is-open
       class behind (breaking :has() scroll lock on next open). */
    el.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeModal();
    });

    /* Defensive sync: any stray close() call we didn't make should
       still clean up class + inert so the next open is clean. */
    el.addEventListener('close', function () {
      if (el.classList.contains('is-open')) closeModal();
    });
  }

  /* ────────────── Cart validation ────────────── */

  /**
   * Sweep the cart for orphan add-ons.
   * If no parent sauna line items remain, remove every add-on line item.
   * Re-entrant guard prevents validation loops with the cart drawer.
   */
  function validateCart(ev) {
    if (validateCart._busy) return Promise.resolve();
    validateCart._busy = true;

    /* Use the cart object from the event detail when the cart drawer
       passed it (saves a redundant /cart.js fetch). Falls back to
       fetching when called directly (e.g. the 3 s retry path) or when
       the dispatcher didn't include the cart (e.g. cross-tab broadcast,
       which intentionally doesn't forward the payload). */
    var cartFromEvent = ev && ev.detail && ev.detail.cart;
    var cartPromise = cartFromEvent
      ? Promise.resolve(cartFromEvent)
      : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });

    return cartPromise
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var hasParent = cart.items.some(function (i) {
          return !i.properties || !i.properties._addon_type;
        });
        /* Cart is healthy — forget any reload budget a previous clean spent,
           so a legitimate clean later in the session is never suppressed. */
        if (hasParent) { cleanReloadCount(null); return; }

        /* No parent sauna left — reset the page-side selection too.
           Covers both the orphan-cleanup path below and the manual-empty
           path (cart emptied with no add-ons in it). */
        resetSelection();

        var addonItems = cart.items.filter(function (i) {
          return i.properties && i.properties._addon_type;
        });
        if (!addonItems.length) { cleanReloadCount(null); return; }

        var updates = {};
        addonItems.forEach(function (i) { updates[i.key] = 0; });

        /* Phase 17: serialize with other cart mutations across tabs. */
        var doUpdate = function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ updates: updates })
          }).then(function (r) {
            /* 2026-09-04 — announce the clean ONLY once the server confirms it.
               This used to fire unconditionally. cart:cleaned makes the cart
               page reload; reloading after a REJECTED removal re-runs this
               sweep on the same unchanged cart, which is rejected again, which
               reloads again. Throwing instead hands the failure to the catch
               below, which is where the single bounded retry already lives. */
            if (!r.ok) throw new Error('cart update rejected: ' + r.status);
            /* Tell the cart drawer to redraw with the cleaned cart. */
            document.dispatchEvent(new CustomEvent('cart:cleaned'));
          });
        };
        return (window.BettrCrossTab && window.BettrCrossTab.withLock)
          ? window.BettrCrossTab.withLock('bettr-cart-mutation', doUpdate)
          : doUpdate();
      })
      .catch(function () {
        /* Network/Shopify hiccup — schedule a single retry so orphans don't
           sit in the cart indefinitely. Guarded so a chain of failures
           doesn't queue infinite retries. */
        if (!validateCart._retryPending) {
          validateCart._retryPending = true;
          setTimeout(function () {
            validateCart._retryPending = false;
            validateCart();
          }, 3000);
        }
      })
      .then(function () { validateCart._busy = false; });
  }

  /**
   * Untick one add-on card by variant id.
   * Used by the PDP add-to-cart retry (assets/custom-pdp-a.js) when a specific
   * add-on came back unavailable: without this the card stays visibly ticked
   * while the cart holds nothing for it, and the next click retries the same
   * failing line.
   */
  function deselectVariant(variantId) {
    var id = String(variantId);
    selected.delete(id);
    var check = document.querySelector('[data-addon-variant-id="' + id + '"]');
    if (check && check.checked) check.checked = false;
    var card = check && check.closest('[data-addon-card]');
    if (card) card.classList.remove('is-checked');
    syncAddCta();
  }

  /* 2026-08-21 — the sweep above only ever ran on cart:updated, and the ONLY
     dispatcher of that event is the cart drawer. The standalone /cart page
     (sections/custom-cart-page.liquid) removes a line by navigating
     item.url_to_remove and edits quantity with a full form POST, so it never
     dispatches anything — a buyer who deleted the sauna there kept the $450
     warranty and the $880 assembly service in a checkout-able cart, forever.
     There is no post-mutation moment on a full page reload, so sweep on LOAD
     instead, gated on the cart page's own marker: every other page pays nothing. */
  var loadSweepDone = false;
  function sweepOnCartPage() {
    if (loadSweepDone) return;
    if (!document.querySelector('[data-cart-page-form]')) return;
    loadSweepDone = true;
    validateCart();
  }

  /* Listen for cart change events from the cart drawer */
  document.addEventListener('cart:updated', validateCart);

  /* The drawer redraws itself from cart:cleaned; the cart page renders its
     lines in Liquid, so a cleaned cart leaves stale rows on screen there.
     Bounded to one reload per page load so a failing /cart/update.js can't
     produce a clean/reload loop. */
  var RELOAD_KEY = 'bettr-cart-clean-reloads';
  var RELOAD_MAX = 2;
  function cleanReloadCount(next) {
    try {
      if (next === undefined) return parseInt(sessionStorage.getItem(RELOAD_KEY), 10) || 0;
      if (next === null) sessionStorage.removeItem(RELOAD_KEY);
      else sessionStorage.setItem(RELOAD_KEY, String(next));
    } catch (e) { /* private mode / storage disabled — fall through */ }
    return 0;
  }

  var reloadedAfterClean = false;
  document.addEventListener('cart:cleaned', function () {
    if (reloadedAfterClean) return;
    if (!document.querySelector('[data-cart-page-form]')) return;
    /* An in-page flag cannot bound a reload loop by itself: every reload starts
       a fresh page with a fresh flag. Count across loads instead, so no cart
       state can ever trap a buyer in a refresh cycle on /cart. The counter is
       cleared as soon as a sweep finds the cart healthy. */
    if (cleanReloadCount() >= RELOAD_MAX) return;
    cleanReloadCount(cleanReloadCount() + 1);
    reloadedAfterClean = true;
    window.location.reload();
  });

  /* ── Public API ── */
  window.BettrAddons = {
    init: init,
    getSelectedItems: getSelectedItems,
    buildCartItems: buildCartItems,
    openModal: openModal,
    closeModal: closeModal,
    validateCart: validateCart,
    deselectVariant: deselectVariant
  };

  /* ── Bootstrap ── */
  /* wireModal() intentionally NOT called here — the modal is in <template>
     and we don't want to mount it on every page load. openModal() will
     wireModal() lazily on the first add-on Learn-more click. */
  function bootstrap() {
    init();
    sweepOnCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* Re-init on Theme Editor section reload */
  document.addEventListener('shopify:section:load', bootstrap);
})();
